// ChangePasswordModal — the /my-profile Password card's Edit flow: current +
// new + confirm, gated on the three rules the checklist spells out. The rules
// are defined here rather than reusing scorePassword() from utils/password,
// which scores a different set (length / upper / digit / symbol) and would
// disagree with the labels the user is reading.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Icon, Modal, PasswordInput } from '@/components/common';
import { useUpdatePasswordMutation } from '@/api/endpoints/ownProfileApi';
import type { ApiErrorShape } from '@/api/types';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

/** The checklist, in render order. Each rule is its own gate on submit. */
const RULES: { key: string; labelKey: string; test: (v: string) => boolean }[] = [
  { key: 'length', labelKey: 'profile_rule_length', test: (v) => v.length >= 8 },
  { key: 'number', labelKey: 'profile_rule_number', test: (v) => /[0-9]/.test(v) },
  {
    key: 'case',
    labelKey: 'profile_rule_case',
    test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v),
  },
];

const emptyDraft = { current: '', next: '', confirm: '' };

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const { t } = useTranslation();
  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();
  const [draft, setDraft] = useState(emptyDraft);

  // Reopening starts clean — a half-typed password must never survive a close.
  useEffect(() => {
    if (!open) return;
    setDraft(emptyDraft);
  }, [open]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const met = RULES.map((r) => r.test(draft.next));
  const rulesPass = met.every(Boolean);
  const confirmMismatch = draft.confirm.length > 0 && draft.confirm !== draft.next;
  const valid = draft.current.length > 0 && rulesPass && draft.confirm === draft.next;

  // Unreachable unless valid — the submit button is disabled until then.
  const submit = async () => {
    try {
      await updatePassword({ oldPassword: draft.current, newPassword: draft.next }).unwrap();
      toast.success(t('profile_password_updated'));
      onClose();
    } catch (err) {
      // The server words "current password is incorrect" better than we can;
      // the modal stays open so a mistype is one correction away.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='lock'
      title={t('profile_change_password_title')}
      className='my-profile__password-modal'
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='check' onClick={submit} loading={isLoading} disabled={!valid}>
            {t('profile_update_password')}
          </Button>
        </>
      }
    >
      <div className='my-profile__password-form'>
        <Field label={t('profile_current_password')} htmlFor='profile-current-password'>
          <PasswordInput
            id='profile-current-password'
            value={draft.current}
            placeholder={t('profile_current_password_ph')}
            onChange={(e) => patch({ current: e.target.value })}
          />
        </Field>

        <Field label={t('profile_new_password')} htmlFor='profile-new-password'>
          <PasswordInput
            id='profile-new-password'
            value={draft.next}
            placeholder={t('profile_new_password_ph')}
            onChange={(e) => patch({ next: e.target.value })}
          />
        </Field>

        <Field
          label={t('profile_confirm_password')}
          htmlFor='profile-confirm-password'
          error={confirmMismatch ? t('profile_password_mismatch') : undefined}
        >
          <PasswordInput
            id='profile-confirm-password'
            value={draft.confirm}
            placeholder={t('profile_confirm_password_ph')}
            error={confirmMismatch}
            onChange={(e) => patch({ confirm: e.target.value })}
          />
        </Field>

        <ul className='my-profile__rules'>
          {RULES.map((rule, i) => (
            <li key={rule.key} className={`my-profile__rule${met[i] ? ' is-met' : ''}`}>
              <Icon name={met[i] ? 'circle-check' : 'circle'} size={16} />
              {t(rule.labelKey)}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
